import { useEffect, useRef, useState } from 'react'
import type { Lesson } from '@/types/lesson'
import type { ContentLang } from '@/features/editor/editorSlice'
import { useGetLessonBlocksQuery } from '@/features/editor/lessonsApi'
import {
  lessonBlockIssues,
  overflowSlot,
  type BlockIssue,
} from '@/features/editor/lib/publishIssues'
import {
  designOverflowCacheKey,
  readOverflowCache,
  type DesignBlock,
} from '@/features/editor/lib/designOverflowCache'
import type { DesignOverflowLevel } from '@/features/editor/lib/designOverflowMeasure'
import { DesignOverflowProbe } from '../blocks/previews/design/DesignOverflowProbe'

type LessonRef = Omit<Lesson, 'blocks'>

/**
 * Экраны ОДНОГО урока — хуки нельзя звать в цикле по урокам (приём `EmptyVideoGate`).
 * Составные блоки с элементами меряются на каждом языке в скрытых холстах; итог
 * сообщается, только когда блоки загружены И каждый замер известен — до того урок
 * «проверяется», и кнопка публикации погашена. Известный итог, ставший снова
 * неизвестным (правка составного — перезамер, перезагрузка блоков), сообщается
 * как `null`: иначе родитель держал бы прежний итог, и кнопка горела бы, пока
 * идёт новый замер, — переполнение api не проверит.
 */
export const LessonIssuesProbe = ({
  lesson,
  rank,
  langs,
  onResult,
}: {
  lesson: LessonRef
  rank: number
  langs: ContentLang[]
  onResult: (lessonId: number, issues: BlockIssue[] | null) => void
}) => {
  const { data: blocks } = useGetLessonBlocksQuery(lesson.id)
  // Итоги замеров этого зонда по ключу кеша — на случай, если кеш ещё не прочитан
  // в рендере (сбой замера в кеш не пишется, а ответ нужен).
  const [measured, setMeasured] = useState<Record<string, DesignOverflowLevel>>({})

  const targets = (blocks ?? [])
    .filter((b): b is DesignBlock => b.type === 'design' && (b.elements ?? []).length > 0)
    .flatMap((block) =>
      langs.map((lang) => {
        const key = designOverflowCacheKey(block, lang)
        return {
          block,
          lang,
          key,
          slot: overflowSlot(block.id, lang),
          value: readOverflowCache(key) ?? measured[key],
        }
      }),
    )
  const pending = targets.filter((t) => t.value === undefined)
  // Помещающиеся (`fits`) в карту не входят: строки списка нужны только жёлтым и красным.
  const overflow = new Map<string, 'warn' | 'over'>(
    targets.flatMap((t) =>
      t.value === 'warn' || t.value === 'over' ? [[t.slot, t.value] as const] : [],
    ),
  )
  const issues =
    blocks && pending.length === 0
      ? lessonBlockIssues(lesson.id, rank, blocks, langs, overflow)
      : null
  // Сигнатура строкой — массивы пересоздаются на каждый рендер (как в `EmptyVideoGate`).
  const signature = issues === null ? '' : JSON.stringify(issues)
  const reportedRef = useRef(false)
  useEffect(() => {
    if (issues !== null) {
      reportedRef.current = true
      onResult(lesson.id, issues)
    } else if (reportedRef.current) {
      reportedRef.current = false
      onResult(lesson.id, null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, issues === null])

  return (
    <>
      {pending.map((t) => (
        // Ключ с элементами: правка блока — новый зонд, старый отпускает слот.
        <DesignOverflowProbe
          key={`${t.slot}|${t.key}`}
          block={t.block}
          lang={t.lang}
          onResult={(level) => setMeasured((prev) => ({ ...prev, [t.key]: level }))}
        />
      ))}
    </>
  )
}
